import { Trash2 } from 'lucide-react';
import {
  deleteAdminAssessment,
  type AdminAssessmentRow,
  type AdminReportStatus,
} from '../../lib/apiClient';
import { ConfirmAction } from './ConfirmAction';

/**
 * The contents of an expanded user row (spec §9).
 *
 * One line per assessment, newest first, in production's order:
 *
 * ```
 * Approval Seeker         [Bottleneck]  8/17/2026  Free #3  Free
 * The Strategic Operator  [Work Fit]    8/17/2026  Free #2  Free
 * ```
 *
 * The line ends with production's trash icon, behind the same two-step inline
 * confirmation every other control uses. **It is the only irreversible action
 * on the console**: the answers, the result and the assessment row go
 * together, and no undo exists in production either.
 */
/**
 * **A production defect, reproduced.** Production's badge is a two-way branch —
 * `assessmentType === "work_fit" ? "Work Fit" : "Bottleneck"` — written before
 * Perspective existed and never widened. A Perspective assessment therefore
 * shows a **Bottleneck** badge in the console, and it does here too.
 *
 * Correcting it would be an improvement, and improvements come after parity.
 */
export function typeBadge(assessmentType: string): string {
  return assessmentType === 'work_fit' ? 'Work Fit' : 'Bottleneck';
}

/**
 * Badge colours, from spec §9: paid and unlocked green, coupon yellow, credit
 * blue, locked muted. `free` is not given a colour there and takes the muted
 * treatment, which is what the observed rows show.
 *
 * **`credit` is `sky`, not `blue`.** The visual system prohibits `blue-*`
 * utilities outright (section B.2), and `sky` is the nearest permitted hue. The
 * choice has no rendered consequence: the badge is unreachable in production
 * and reproduced as unreachable here.
 */
const STATUS_STYLES: Record<AdminReportStatus, string> = {
  free: 'bg-secondary text-secondary-foreground',
  paid: 'bg-green-500/15 text-green-500',
  unlocked: 'bg-green-500/15 text-green-500',
  coupon: 'bg-yellow-500/15 text-yellow-500',
  credit: 'bg-sky-500/15 text-sky-500',
};

const STATUS_LABELS: Record<AdminReportStatus, string> = {
  free: 'Free',
  paid: 'Paid',
  coupon: 'Coupon',
  credit: 'Credit',
  unlocked: 'Unlocked',
};

/**
 * Production's attempt tag: the first three assessments a customer takes are
 * free, and everything after is a retake numbered from one. The sequence spans
 * every assessment type together, so a customer's fourth assessment is
 * `Retake #1` whichever type it was.
 */
export function attemptTag(sequence: number): string {
  return sequence <= 3 ? `Free #${sequence}` : `Retake #${sequence - 3}`;
}

export function AssessmentHistory({
  assessments,
  token,
  onChanged,
}: {
  assessments: AdminAssessmentRow[];
  token: string | null;
  onChanged: () => void;
}) {
  return (
    <div className="flex flex-col gap-2" data-testid="assessment-history">
      {assessments.map((assessment) => (
        <div
          key={assessment.id}
          className="flex flex-wrap items-center gap-3 text-sm"
          data-testid="assessment-row"
          data-result-id={assessment.id}
        >
          <span className="font-medium" data-testid="assessment-name">
            {assessment.primaryType}
          </span>

          <span
            className={`rounded px-1.5 py-0.5 text-xs font-medium ${
              assessment.assessmentType === 'work_fit'
                ? 'bg-sky-500/10 text-sky-500'
                : 'bg-primary/10 text-primary'
            }`}
            data-testid="assessment-type"
            data-assessment-type={assessment.assessmentType}
          >
            {typeBadge(assessment.assessmentType)}
          </span>

          <span className="text-muted-foreground" data-testid="assessment-date">
            {assessment.completedAt ? localeDate(assessment.completedAt) : null}
          </span>

          {/*
            Production paints the tag with the pill the sequence earns: the
            three free attempts muted, every retake in the accent.
          */}
          <span
            className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
              assessment.sequence <= 3
                ? 'border-border bg-secondary text-muted-foreground'
                : 'border-primary/40 bg-primary/10 text-primary'
            }`}
            data-testid="assessment-attempt"
          >
            {attemptTag(assessment.sequence)}
          </span>

          {assessment.reportStatus ? (
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[assessment.reportStatus]}`}
              data-testid="assessment-status"
              data-status={assessment.reportStatus}
            >
              {STATUS_LABELS[assessment.reportStatus]}
            </span>
          ) : null}

          <PurchaseDetail assessment={assessment} />

          <ConfirmAction
            testId="action-delete-assessment"
            label="Delete assessment"
            pendingLabel="Deleting..."
            onConfirm={async () =>
              token !== null && (await deleteAdminAssessment(token, assessment.id))
            }
            onChanged={onChanged}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </ConfirmAction>
        </div>
      ))}
    </div>
  );
}

/**
 * The purchase detail block, on the right of the row:
 *
 * ```
 * List: $79.00   Discount: $20.00   Collected: $59.00   SAVE20   a1b2c3d4e5f6…
 * ```
 *
 * Production draws it only when the row has a purchase, and each part of it
 * only when that part has something to say — a `$0.00` discount is omitted, a
 * `paid` payment status is omitted, and a `Collected:` of nothing reads `—`.
 * The suppressed `paid` is the point of that column: what an operator needs to
 * see is the sale that *didn't* settle.
 */
function PurchaseDetail({ assessment: a }: { assessment: AdminAssessmentRow }) {
  // Production's own test for whether the block exists at all.
  const hasPurchase = a.purchaseListAmount !== null || a.purchaseAmountPaid !== null;
  if (!hasPurchase) return null;

  const listPrice = money(a.purchaseListAmount);
  const discount = money(a.purchaseDiscountAmount);
  const collected = money(a.purchaseAmountPaid);
  const session = shortSession(a.purchaseStripeSessionId);

  return (
    <span
      className="flex flex-wrap items-center justify-end gap-3 text-xs text-muted-foreground"
      data-testid="assessment-purchase"
    >
      {listPrice ? (
        <span data-testid="purchase-list">
          List: <span className="text-foreground/70">{listPrice}</span>
        </span>
      ) : null}

      {/* A discount of nothing is not a discount, and production omits it. */}
      {discount && discount !== '$0.00' ? (
        <span data-testid="purchase-discount">
          Discount: <span className="text-yellow-500">{discount}</span>
        </span>
      ) : null}

      <span data-testid="purchase-collected">
        Collected:{' '}
        <span className={collected === '$0.00' ? 'text-yellow-500' : 'font-medium text-green-500'}>
          {collected ?? '—'}
        </span>
      </span>

      {a.purchaseCouponCode ? (
        <span
          className="rounded bg-yellow-500/10 px-1.5 py-0.5 font-mono text-yellow-500"
          data-testid="purchase-coupon"
        >
          {a.purchaseCouponCode}
        </span>
      ) : null}

      {/* Shown only when Stripe says something other than "paid". */}
      {a.purchasePaymentStatus && a.purchasePaymentStatus !== 'paid' ? (
        <span className="text-muted-foreground/60" data-testid="purchase-payment-status">
          {a.purchasePaymentStatus.replace(/_/g, ' ')}
        </span>
      ) : null}

      {session ? (
        <span className="font-mono text-muted-foreground/40" data-testid="purchase-session">
          {session}
        </span>
      ) : null}
    </span>
  );
}

/** `$X.XX` from cents, or null when there is no amount at all. */
function money(cents: number | null): string | null {
  return cents === null ? null : `$${(cents / 100).toFixed(2)}`;
}

/**
 * Production abbreviates the Stripe session id: the `cs_test_` / `cs_live_`
 * prefix goes, then the first twelve characters and an ellipsis. Long enough to
 * find the session in Stripe, short enough not to take the row over.
 */
export function shortSession(sessionId: string | null): string | null {
  if (!sessionId) return null;
  return `${sessionId.replace(/^cs_(test_|live_)?/, '').slice(0, 12)}…`;
}

function localeDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}
